// powerliftingFormulas.js
// Fórmulas de puntaje relativo al peso corporal: DOTS, Wilks (2017) y GL Points (2020).
// Todas reciben el peso corporal y el levantamiento en kg.

export const KG_PER_LB = 0.45359237;
export const LB_PER_KG = 2.2046226;

const DOTS_COEFFS = {
  Male: { a: -0.000001093, b: 0.0007391293, c: -0.1918759221, d: 24.0900756, e: -307.75076, min: 40, max: 210 },
  Female: { a: -0.0000010706, b: 0.0005158568, c: -0.1126655495, d: 13.6175032, e: -57.96288, min: 40, max: 150 },
};

const WILKS_COEFFS = {
  Male: { c: [-216.0475144, 16.2606339, -0.002388645, -0.00113732, 7.01863e-6, -1.291e-8], min: 40, max: 201.9 },
  Female: { c: [594.31747775582, -27.23842536447, 0.82112226871, -0.00930733913, 4.731582e-5, -9.054e-8], min: 26.51, max: 154.53 },
};

// GL Points (2020): coef = 100 / (A - B * e^(-C * bw))
const GL_COEFFS = {
  Male: {
    raw: { total: [1199.72839, 1025.18162, 0.00921], bench: [320.98041, 281.40258, 0.01008] },
    equipped: { total: [1236.25115, 1449.21864, 0.01644], bench: [381.22073, 733.79378, 0.02398] },
  },
  Female: {
    raw: { total: [610.32796, 1045.59282, 0.03048], bench: [142.40398, 442.52671, 0.04724] },
    equipped: { total: [758.63878, 949.31382, 0.02435], bench: [221.82209, 357.00377, 0.02937] },
  },
};

const clamp = (x, lo, hi) => Math.min(Math.max(x, lo), hi);
const valid = (bw, lift) => Number.isFinite(bw) && Number.isFinite(lift) && bw > 0 && lift > 0;

export function dots(gender, bodyweightKg, liftKg) {
  const k = DOTS_COEFFS[gender];
  if (!k || !valid(bodyweightKg, liftKg)) return null;
  const x = clamp(bodyweightKg, k.min, k.max);
  const denom = k.a * x ** 4 + k.b * x ** 3 + k.c * x ** 2 + k.d * x + k.e;
  return (liftKg * 500) / denom;
}

export function wilks(gender, bodyweightKg, liftKg) {
  const k = WILKS_COEFFS[gender];
  if (!k || !valid(bodyweightKg, liftKg)) return null;
  const x = clamp(bodyweightKg, k.min, k.max);
  const denom = k.c.reduce((acc, coef, i) => acc + coef * x ** i, 0);
  return (liftKg * 500) / denom;
}

/**
 * GL Points solo está definido para total de 3 movimientos y para press banca.
 * @param {'total'|'bench'} kind
 * @param {boolean} equipped
 */
export function glPoints(gender, bodyweightKg, liftKg, kind, equipped) {
  const k = GL_COEFFS[gender]?.[equipped ? 'equipped' : 'raw']?.[kind];
  if (!k || !valid(bodyweightKg, liftKg) || bodyweightKg < 35) return null;
  const [A, B, C] = k;
  return (liftKg * 100) / (A - B * Math.exp(-C * bodyweightKg));
}

/** Asigna la categoría de peso a partir del peso corporal (kg). */
export function weightClassFor(bodyweightKg, classes) {
  if (!Number.isFinite(bodyweightKg) || bodyweightKg <= 0 || !classes?.length) return null;
  for (const wc of classes) {
    if (wc.endsWith('+')) return wc;
    if (bodyweightKg <= parseFloat(wc)) return wc;
  }
  return classes[classes.length - 1];
}
